import { useState, type KeyboardEvent } from 'react';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Download,
  ExternalLink,
  Image as ImageIcon,
  MessageSquare,
} from 'lucide-react';
import { useTranslate } from 'ra-core';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';

/** Completion notes use actions except for open-ended answers, which use response. */
export type ChecklistTaskCompletion =
  | { version: 1; type: 'TASK'; actions?: string }
  | { version: 1; type: 'OPEN_ENDED'; response?: string }
  | {
      version: 1;
      type: 'RADIO_SELECT';
      selectedOptions: string[];
      actions?: string;
    }
  | {
      version: 1;
      type: 'PHOTO_UPLOAD';
      imageUrls: string[];
      actions?: string;
    };

export interface TaskResponseDialogRecord {
  checklistSnapshotTitle: string;
  taskSnapshotTitle: string;
  unitSnapshotName: string;
  taskCompletedByFirstName?: string;
  taskCompletedByLastName?: string;
  taskCompletionDetails?: { completion: ChecklistTaskCompletion };
}

type TaskResponsesDialogProps = {
  open: boolean;
  record: TaskResponseDialogRecord | null;
  onClose: () => void;
};

export function TaskResponsesDialog({
  open,
  record,
  onClose,
}: TaskResponsesDialogProps) {
  const translate = useTranslate();
  const [selectedTab, setSelectedTab] = useState('response');
  const [selectedPhoto, setSelectedPhoto] = useState(0);

  if (!record) return null;

  const completion = record.taskCompletionDetails?.completion;
  const hasResponse =
    completion?.type === 'OPEN_ENDED' ||
    completion?.type === 'RADIO_SELECT' ||
    completion?.type === 'PHOTO_UPLOAD';
  const comment =
    completion && 'actions' in completion ? completion.actions : undefined;
  const hasComment = Boolean(
    completion && completion.type !== 'OPEN_ENDED' && completion.actions?.trim()
  );
  const activeTab = hasResponse
    ? selectedTab === 'comment' && hasComment
      ? 'comment'
      : 'response'
    : 'comment';
  const completedBy = [
    record.taskCompletedByFirstName,
    record.taskCompletedByLastName,
  ]
    .filter(Boolean)
    .join(' ');
  const images =
    completion?.type === 'PHOTO_UPLOAD' ? completion.imageUrls : [];
  const photoIndex = images.length ? selectedPhoto % images.length : 0;

  const handlePhotoKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      completion?.type !== 'PHOTO_UPLOAD' ||
      activeTab !== 'response' ||
      images.length === 0 ||
      (event.target instanceof HTMLElement &&
        event.target.closest(
          '[role="tab"], input, textarea, select, [contenteditable="true"]'
        ))
    ) {
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const delta = event.key === 'ArrowLeft' ? -1 : 1;
      setSelectedPhoto(
        (current) => (current + delta + images.length) % images.length
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-3xl"
        onKeyDownCapture={handlePhotoKeyDown}
      >
        <DialogHeader className="min-w-0 pr-8">
          <DialogTitle>
            {translate('moonstar.checklists.responses.title')}
          </DialogTitle>
          <DialogDescription className="truncate">
            {record.checklistSnapshotTitle} - {record.taskSnapshotTitle} -{' '}
            {record.unitSnapshotName}
            {completedBy ? ` - ${completedBy}` : ''}
          </DialogDescription>
        </DialogHeader>

        {hasResponse || hasComment ? (
          <Tabs value={activeTab} onValueChange={setSelectedTab}>
            <TabsList
              variant="line"
              aria-label={translate('moonstar.checklists.responses.sections')}
            >
              {hasResponse && (
                <TabsTrigger value="response">
                  {completion.type === 'PHOTO_UPLOAD' ? (
                    <>
                      <ImageIcon />
                      {translate('moonstar.checklists.responses.photos', {
                        count: images.length,
                      })}
                    </>
                  ) : (
                    <>
                      <ClipboardList />
                      {translate('moonstar.checklists.responses.response')}
                    </>
                  )}
                </TabsTrigger>
              )}
              {hasComment && (
                <TabsTrigger value="comment">
                  <MessageSquare />
                  {translate('moonstar.checklists.responses.comment')}
                </TabsTrigger>
              )}
            </TabsList>

            {hasResponse && (
              <TabsContent value="response">
                {completion.type === 'PHOTO_UPLOAD' ? (
                  <PhotoResponse
                    images={images}
                    selectedIndex={photoIndex}
                    onSelect={setSelectedPhoto}
                  />
                ) : completion.type === 'OPEN_ENDED' ? (
                  <div className="rounded-md border bg-muted/50 p-5">
                    {completion.response}
                  </div>
                ) : (
                  <div className="rounded-md border bg-muted/50 p-5">
                    <p className="mb-3 font-semibold text-primary">
                      {record.taskSnapshotTitle}
                    </p>
                    {completion.selectedOptions.length ? (
                      <div className="flex flex-col gap-2">
                        {completion.selectedOptions.map((option, index) => (
                          <div
                            key={`${option}-${index}`}
                            className="flex min-h-10 items-center gap-2 rounded-md border border-primary px-3"
                          >
                            <span
                              className="flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary bg-primary text-primary-foreground"
                              aria-hidden="true"
                            >
                              <Check className="size-3" />
                            </span>
                            {option}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">
                        {translate('moonstar.checklists.responses.noOptions')}
                      </p>
                    )}
                  </div>
                )}
              </TabsContent>
            )}
            {hasComment && (
              <TabsContent value="comment">
                <div className="rounded-md border bg-muted/50 p-5">
                  <p>{comment}</p>
                  {completedBy && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      {translate('moonstar.checklists.responses.addedBy', {
                        name: completedBy,
                      })}
                    </p>
                  )}
                </div>
              </TabsContent>
            )}
          </Tabs>
        ) : (
          <p className="text-muted-foreground">
            {translate('moonstar.checklists.responses.empty')}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PhotoResponse({
  images,
  selectedIndex,
  onSelect,
}: {
  images: string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}) {
  const translate = useTranslate();

  if (!images.length) {
    return (
      <p className="text-muted-foreground">
        {translate('moonstar.checklists.responses.noPhotos')}
      </p>
    );
  }

  const currentPhoto = images[selectedIndex];
  const movePhoto = (delta: number) =>
    onSelect((selectedIndex + delta + images.length) % images.length);

  return (
    <div className="min-w-0">
      <img
        src={currentPhoto}
        alt={translate('moonstar.checklists.responses.photo', {
          number: selectedIndex + 1,
        })}
        className="h-60 w-full rounded-md bg-muted object-cover sm:h-96"
      />
      <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={translate('moonstar.checklists.responses.previousPhoto')}
          onClick={() => movePhoto(-1)}
        >
          <ChevronLeft />
        </Button>
        <span className="text-sm text-muted-foreground" aria-live="polite">
          {selectedIndex + 1} / {images.length}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={translate('moonstar.checklists.responses.nextPhoto')}
          onClick={() => movePhoto(1)}
        >
          <ChevronRight />
        </Button>
        <a
          href={currentPhoto}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <ExternalLink className="size-4" />
          {translate('moonstar.checklists.responses.fullSize')}
        </a>
        <a
          href={currentPhoto}
          download
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <Download className="size-4" />
          {translate('moonstar.checklists.responses.download')}
        </a>
      </div>
      <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
        {images.map((image, index) => (
          <button
            key={`${image}-${index}`}
            type="button"
            aria-label={translate('moonstar.checklists.responses.showPhoto', {
              number: index + 1,
            })}
            aria-pressed={selectedIndex === index}
            onClick={() => onSelect(index)}
            className={`size-16 shrink-0 overflow-hidden rounded-md border-2 bg-background focus-visible:outline-2 focus-visible:outline-ring ${
              selectedIndex === index ? 'border-primary' : 'border-border'
            }`}
          >
            <img src={image} alt="" className="size-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}
